import { get, post, type QueryParams } from '../../lib/api/client';
import { downloadExport } from '../../lib/api/download';
import { fromP1, toP1Query, type P1Response, type Page, type PageRequest } from '../../lib/api/paging';

/**
 * The profile directory (BE `core/api/handler/admin-profiles.go`). ONE
 * endpoint serves Chefs and Eaters: a chef is a consumer profile with a
 * published (non-draft) listing, an eater is any consumer profile. The
 * difference is `hasPublishedListing`.
 */

/** Derived from the last publish date, not stored (BE analytics.chef windows). */
export type ChefStatus = 'active' | 'inactive' | 'gone';
export type BlueStarStatus = 'none' | 'pending' | 'approved' | 'declined';
/** Stored account status (`Profile.status`). Deactivate sets `inactive`. */
export type ProfileStatus = 'pending' | 'active' | 'suspended' | 'removed' | 'inactive' | 'deleted';

export const BLUE_STAR_STATUSES: BlueStarStatus[] = ['none', 'pending', 'approved', 'declined'];

/** BE `adminProfileOrder` allowlist; anything else sorts by joinedAt. */
export type ProfileSortKey =
  | 'displayName'
  | 'joinedAt'
  | 'orderCount'
  | 'totalPublished'
  | 'totalSold'
  | 'avgRating'
  | 'eaterAvgRating'
  | 'lastActiveAt';

/** `model.AdminProfileRow`. */
export interface AdminProfileRow {
  profileId: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  persona: string;
  /** Chef status — derived from the last publish. Meaningless for a profile that never published. */
  status: ChefStatus;
  blueStarStatus: BlueStarStatus;
  joinedAt: string;
  /** The last PUBLISH, not the last login or order. */
  lastActiveAt: string | null;
  totalPublished: number;
  /** Portions on completed orders. */
  totalSold: number;
  /** Orders this profile PLACED (lifetime, `profiles.order_count`). */
  orderCount: number;
  /** null = never reviewed, which is not a low rating. */
  avgRating: number | null;
  reviewCount: number;
  /** Chefs rating this profile as an eater (`chef_review`). null = never reviewed. */
  eaterAvgRating: number | null;
  eaterReviewCount: number;
  listerBalanceCents: number;
}

export interface DirectoryFilters {
  /** Chefs: true. Eaters: omitted. */
  hasPublishedListing?: boolean;
  /**
   * Only profiles whose user never finished registration. Without it the BE
   * lists registered users only.
   */
  unregistered?: boolean;
  /**
   * Test / demo accounts: false leaves them out (the Chefs and Eaters lists),
   * true lists only them; omitted lists everyone (e.g. a campaign's people).
   */
  demo?: boolean;
  status?: ChefStatus;
  blueStarStatus?: BlueStarStatus;
  localeIds?: string[];
  searchTerm?: string;
  /** YYYY-MM-DD in the admin's profile timezone; *End includes the whole day. */
  signupStart?: string;
  signupEnd?: string;
  lastActiveStart?: string;
  lastActiveEnd?: string;
  ratingMin?: number;
  ratingMax?: number;
  orderCountMin?: number;
  orderCountMax?: number;
  sortBy?: ProfileSortKey;
  order?: 'asc' | 'desc';
}

export interface DirectoryParams extends DirectoryFilters, PageRequest {}

function directoryQuery(f: DirectoryFilters): QueryParams {
  return {
    persona: 'consumer',
    hasPublishedListing: f.hasPublishedListing ? 'true' : undefined,
    registered: f.unregistered ? 'false' : undefined,
    demo: f.demo === undefined ? undefined : String(f.demo),
    status: f.status,
    blueStarStatus: f.blueStarStatus,
    localeIds: f.localeIds,
    searchTerm: f.searchTerm?.trim(),
    signupStart: f.signupStart,
    signupEnd: f.signupEnd,
    lastActiveStart: f.lastActiveStart,
    lastActiveEnd: f.lastActiveEnd,
    ratingMin: f.ratingMin,
    ratingMax: f.ratingMax,
    orderCountMin: f.orderCountMin,
    orderCountMax: f.orderCountMax,
    sortBy: f.sortBy,
    order: f.sortBy ? f.order : undefined,
  };
}

export async function listProfiles(
  { page, pageSize, ...filters }: DirectoryParams,
  signal?: AbortSignal,
): Promise<Page<AdminProfileRow>> {
  const res = await get<P1Response<AdminProfileRow>>(
    '/admin/profiles',
    { ...directoryQuery(filters), ...toP1Query({ page, pageSize }) },
    signal,
  );
  return fromP1(res);
}

/** Same filters as the list, no paging. The BE refuses (400) over 100k rows. */
export function exportProfiles(filters: DirectoryFilters, resource: string): Promise<void> {
  return downloadExport('/admin/profiles/export.csv', resource, directoryQuery(filters));
}

// --- detail -----------------------------------------------------------------

/** The subset of `model.Profile` the dialogs read. */
export interface AdminProfile {
  id: string;
  createdAt: string;
  status: ProfileStatus;
  /** A test / demo account — left out of the Chefs and Eaters lists. */
  demo?: boolean;
  displayName: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneE164?: string;
  avatarUrl?: string;
  bio?: string;
  timezone: string;
  currentLocaleId?: string;
  blueStarStatus: BlueStarStatus;
  orderCount: number;
  listerBalanceCents: number;
}

export interface AdminNote {
  id: string;
  /** RFC 3339, UTC. */
  at: string;
  byProfileId: string;
  text: string;
}

/** `model.Order` fields the order history shows. Money in cents. */
export interface OrderSummary {
  id: string;
  orderNumber: string;
  status: string;
  listingId: string;
  quantity: number;
  fulfillmentMethod: string;
  pricePerUnit: number;
  subtotal: number;
  totalPaid: number;
  createdAt: string;
  completedAt?: string;
}

export interface ProfileDetail {
  profile: AdminProfile;
  /** The directory row for this profile — null when it is not a consumer. */
  computed: AdminProfileRow | null;
  /** The 50 most recent orders this profile PLACED, newest first. */
  orderHistory: OrderSummary[];
  adminNotes: AdminNote[];
  notImplemented: Record<string, string>;
}

export async function getProfileDetail(id: string, signal?: AbortSignal): Promise<ProfileDetail> {
  const res = await get<ProfileDetail>(`/admin/profiles/${encodeURIComponent(id)}/detail`, undefined, signal);
  return {
    ...res,
    orderHistory: res.orderHistory ?? [],
    adminNotes: res.adminNotes ?? [],
    notImplemented: res.notImplemented ?? {},
  };
}

// --- chef dish history --------------------------------------------------------

export interface ChefListing {
  listingId: string;
  title: string;
  dishStatus: string;
  /** Cents. */
  price: number | null;
  quantity: number | null;
  localeId: string | null;
  publishedAt: string | null;
  createdAt: string;
  /** Completed orders. */
  ordersPlaced: number;
  /** Portions on completed orders. */
  portionsSold: number;
}

export interface ListingsParams extends PageRequest {
  includeDrafts?: boolean;
  dishStatus?: string;
  startDate?: string;
  endDate?: string;
}

export async function listChefListings(
  id: string,
  { page, pageSize, includeDrafts, ...rest }: ListingsParams,
  signal?: AbortSignal,
): Promise<Page<ChefListing>> {
  const res = await get<P1Response<ChefListing>>(
    `/admin/profiles/${encodeURIComponent(id)}/listings`,
    { ...rest, includeDrafts: includeDrafts ? 'true' : undefined, ...toP1Query({ page, pageSize }) },
    signal,
  );
  return fromP1(res);
}

// --- writes -------------------------------------------------------------------

export function setBlueStarStatus(id: string, status: BlueStarStatus): Promise<AdminProfile> {
  return post<AdminProfile>(`/admin/profiles/${encodeURIComponent(id)}/blue-star-status`, { status });
}

/** Append-only. Returns the full list. */
export async function addAdminNote(id: string, text: string): Promise<AdminNote[]> {
  const res = await post<{ adminNotes: AdminNote[] | null }>(`/admin/profiles/${encodeURIComponent(id)}/notes`, {
    text,
  });
  return res.adminNotes ?? [];
}

/** Sets the account to `inactive` (reversible). A reason is stored as an admin note. */
/** Marks a profile as a test / demo account, or clears it (kept as an admin note). */
export function setProfileDemo(id: string, demo: boolean): Promise<AdminProfile> {
  return post<AdminProfile>(`/admin/profiles/${encodeURIComponent(id)}/demo`, { demo });
}

export function deactivateProfile(id: string, reason?: string): Promise<AdminProfile> {
  return post<AdminProfile>(`/admin/profiles/${encodeURIComponent(id)}/deactivate`, reason ? { reason } : {});
}

// --- user message (BE `core/api/handler/admin-user-message.go`) ----------------

export type UserMessageChannel = 'email' | 'sms';
/** Supported languages (BE `i18n.supportedLanguages`). */
export type UserMessageLang = 'en' | 'es-MX';

/** BE `request.UserMessageSmsMaxChars`: Twilio's 1,600 less the link line. */
export const USER_MESSAGE_SMS_MAX_CHARS = 1500;
/** The Tally form id the www `/connect` page accepts for `?f=`. */
export const USER_MESSAGE_FORM_ID = /^[A-Za-z0-9]{1,12}$/;

export interface SendUserMessageInput {
  profileId: string;
  message: string;
  channel: UserMessageChannel;
  lang: UserMessageLang;
  formId: string;
}

/** The notification row the send wrote. */
export interface UserMessageResult {
  id: string;
  ref: string;
  channel: UserMessageChannel;
  /** `skipped_disabled`: the channel is turned off in this environment's config. */
  status: 'sent' | 'skipped_disabled' | 'failed' | 'pending';
  error?: string;
}

export function sendUserMessage(input: SendUserMessageInput): Promise<UserMessageResult> {
  return post<UserMessageResult>('/admin/send-user-message', input);
}

/** The message translated into `lang`, for the admin to read and edit before sending. */
export async function translateUserMessage(text: string, lang: UserMessageLang): Promise<string> {
  const res = await post<{ text: string }>('/admin/send-user-message/translate', { text, lang });
  return res.text;
}

// --- "No first dish" ------------------------------------------------------------

export interface NeverPublished {
  profileId: string;
  displayName: string;
  signupAt: string;
  hoursSinceSignup: number;
}

export interface TimeToFirstDish {
  avgHoursToFirstDish: number;
  chefsMeasured: number;
  /** Capped (limit ≤ 500), oldest signup first — a worklist, not an export. */
  chefsNeverPublished: NeverPublished[];
}

export async function getTimeToFirstDish(
  { localeIds, limit }: { localeIds?: string[]; limit?: number },
  signal?: AbortSignal,
): Promise<TimeToFirstDish> {
  const res = await get<TimeToFirstDish>('/admin/dashboard/time-to-first-dish', { localeIds, limit }, signal);
  return { ...res, chefsNeverPublished: res.chefsNeverPublished ?? [] };
}

// --- reviews received -----------------------------------------------------------

/** Which side of the profile was reviewed: as a chef (by eaters) or as an eater (by chefs). */
export type ReviewedAs = 'chef' | 'eater';

/** `model.ProfileReviewRow`. */
export interface ProfileReview {
  id: string;
  rating: number;
  note?: string;
  reviewerProfileId: string;
  reviewerDisplayName: string;
  orderId: string;
  orderNumber: string;
  listingId: string;
  dishTitle: string;
  submittedAt: string;
}

/** `GET /admin/profiles/{id}/reviews?as=` — newest first, P1. */
export async function listProfileReviews(
  id: string,
  as: ReviewedAs,
  page: PageRequest,
  signal?: AbortSignal,
): Promise<Page<ProfileReview>> {
  const res = await get<P1Response<ProfileReview>>(
    `/admin/profiles/${encodeURIComponent(id)}/reviews`,
    { as, ...toP1Query(page) },
    signal,
  );
  return fromP1(res);
}
